import { useState } from 'react'
import { Users, Shield, Upload } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useUserCheck } from '../hooks/api/useUserCheck'
import { DashboardLayout } from '../components/layout/DashboardLayout'
import { ChangePasswordModal } from '../components/modals/ChangePasswordModal'
import { PasswordSuccessModal } from '../components/modals/PasswordSuccessModal'

type TabType = 'profile' | 'security'

export const SettingsPage = () => {
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const [activeTab, setActiveTab] = useState<TabType>('profile')
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  // Form state
  const [firstName, setFirstName] = useState(userRecord?.full_name?.split(' ')[0] || '')
  const [lastName, setLastName] = useState(userRecord?.full_name?.split(' ')[1] || '')
  const [email, setEmail] = useState(user?.email || '')

  const handleSaveChanges = () => {
    // TODO: Implement save profile changes
    console.log('Saving profile changes:', { firstName, lastName, email })
  }

  const handlePasswordChange = (passwordData: any) => {
    // TODO: Implement password change via API
    console.log('Changing password:', passwordData)
    setShowPasswordModal(false)
    setShowSuccessModal(true)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">User not found in database!</p>
            <p className="text-red-500 text-xs">Please try signing in again.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500 mt-1">Manage your account, notifications, and security preferences</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center">
          <div className="inline-flex items-center gap-2 p-1 bg-gray-50 rounded-full">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-5 py-2.5 text-sm font-medium rounded-full transition-colors flex items-center gap-2 ${
                activeTab === 'profile'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="w-4 h-4" />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`px-5 py-2.5 text-sm font-medium rounded-full transition-colors flex items-center gap-2 ${
                activeTab === 'security'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Shield className="w-4 h-4" />
              Security
            </button>
          </div>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl mx-auto bg-white rounded-3xl border border-gray-200 p-8">
            <div className="flex items-start gap-3 mb-8">
              <Users className="w-5 h-5 text-gray-900 mt-0.5" />
              <h2 className="text-xl font-bold text-gray-900">Profile Settings</h2>
            </div>

            {/* Avatar Section */}
            <div className="flex items-center gap-6 mb-8">
              <div className="w-24 h-24 bg-gray-900 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                {userRecord.full_name
                  ?.split(' ')
                  .map((name: string) => name.charAt(0))
                  .join('')
                  .toUpperCase() || 'U'}
              </div>
              <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <Upload className="w-4 h-4" />
                Change Avatar
              </button>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-900 mb-2">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Guiddione"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-900 mb-2">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Machava"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="mb-8">
              <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="guiddilonemachava@gmail.com"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveChanges}
              className="w-full md:w-auto px-8 py-3 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors"
            >
              Save Changes
            </button>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="max-w-2xl mx-auto bg-white rounded-3xl border border-gray-200 p-8">
            <div className="flex items-start gap-3 mb-8">
              <Shield className="w-5 h-5 text-gray-900 mt-0.5" />
              <h2 className="text-xl font-bold text-gray-900">Security Settings</h2>
            </div>

            {/* Change Password Option */}
            <button
              onClick={() => setShowPasswordModal(true)}
              className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-left"
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-gray-700" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Change Password</h3>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={handlePasswordChange}
      />

      {/* Success Modal */}
      <PasswordSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </DashboardLayout>
  )
}
